import { QueryClient } from '@tanstack/react-query'
import { SessionExpiredError } from '../services/client'

/**
 * Every read in this app goes through here, so these defaults are a spending decision as
 * much as a UX one — and this is the app whose read volume grows with the business, since
 * it is the one real customers open.
 *
 * Same configuration as the partner console's, deliberately: one behaviour to reason
 * about across all three web apps. See frontend-partner/src/config/queryClient.js.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Thirty seconds of "fresh".
       *
       * Long enough that browsing the directory and stepping in and out of a partner's
       * page costs one request rather than several, and short enough that nothing a
       * customer reads is meaningfully old. The screen that must be fresher — an order
       * being tracked while a rider is on the way — says so at its own call site.
       */
      staleTime: 30_000,

      /**
       * Kept on, and it is not in tension with the line above: a refocus only refetches
       * data that has gone stale. A customer switches away while waiting for food and
       * comes back to check — that is exactly when their order should be current.
       */
      refetchOnWindowFocus: true,

      /**
       * Retrying a refusal is just spending the rate limit to be told no again. A 4xx is
       * an answer: wrong role, missing record, invalid filter. Only genuine failures —
       * network, 5xx — are worth a second attempt.
       *
       * `SessionExpiredError` is never retried: the client already tried a refresh and
       * the refresh itself failed.
       */
      retry: (failureCount, error) => {
        if (error instanceof SessionExpiredError) return false

        const status = error?.status ?? 0
        if (status >= 400 && status < 500) return false

        return failureCount < 2
      },
    },
  },
})
